import type {
  AtcInput,
  AtcPersonList,
  AtcStandard,
  AtcStandardList,
  AtcVersion,
  RunLedgerRow,
} from '@capsio/contracts';
import type { ReactNode } from 'react';

/**
 * ATC 卡页的九个分区（C-186 内容 / C-188 块化）。
 *
 * **卡页不是模型的一个视图——卡页就是那份定义本身。**
 * 九个分区各自钉住至少一个读者的一条硬需要（人 / AI / 监督卡控 / 记录版本），
 * 页面上每一个可见的数都能指到「表.列」或视图——不存在无数据支撑的展示。
 *
 * 呈现层照原型（web/prototype/v0.22）：内容是若干界限清楚的块（.blk），
 * 层次由容器、间距、色彩承载，不靠读者在文字流里找。
 * 每条照抄的量值在 styles.css 里带原型行号注释。
 *
 * ## 三态互斥（文案层 + 视觉层）
 *
 * | 态 | 含义 | 表达 |
 * |---|---|---|
 * | 零态 | 有表有列，当前无数据 | 照实短语：「还没填」「尚未指派责任人」「0」（.zero / .val 虚线块） |
 * | 无源 | 无表无列 | 不占格（蓝图本体一处例外：照实说在等什么） |
 * | 待建 | 模型已确认需要、尚未建 | 统一 `ToBeBuilt` 虚线块 + chip 标记——形状与文字双信号，不靠颜色单独承载 |
 *
 * **不为让分区看起来完整而编内容。待建就标待建。**
 */

/** 待建：模型已确认需要、尚未建。虚线块 + chip——形状与文字双信号，色盲也可读。 */
export function ToBeBuilt({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="tbb">
      <span className="chip wait">待建</span> {children}
    </div>
  );
}

/** 成熟度六级：这是「AI 可以自己走多远」的刻度，不是模糊描述。 */
export const MATURITY_LABEL: Record<string, string> = {
  L0: 'L0 人工',
  L1: 'L1 AI 辅助',
  L2: 'L2 人机协同',
  L3: 'L3 Agent 执行',
  L4: 'L4 自动闭环',
  L5: 'L5 自主优化',
};

const DETERMINISM_LABEL: Record<string, string> = {
  deterministic: '确定性',
  exploratory: '探索性',
};

export const TRIGGER_LABEL: Record<string, string> = {
  schedule: '定时触发',
  event: '事件触发',
  manual: '手动触发',
  undetermined: '未定',
};

const ACTOR_LABEL: Record<string, string> = {
  hub: '智能体',
  human: '人工',
  hybrid: '人机协同',
};

const ROLE_LABEL: Record<string, string> = {
  responsible: '责任人',
  user: '使用者',
  evaluator: '评价者',
};

function orFallback(value: string | null | undefined, fallback: string): ReactNode {
  return value === null || value === undefined || value.trim() === '' ? (
    <span className="zero">{fallback}</span>
  ) : (
    value
  );
}

/** 这一版是谁做的。推断出来的值必须可识别——推断冒充事实是造假。 */
export function authoredByText(v: AtcVersion): string {
  const who = v.authoredBy === 'agent' ? 'AI' : '人工';
  return v.authoredByInferred ? `${who}（回填推断）` : who;
}

/** 执行计量。三个数全部由执行台账（taskrun）逐次派生——是统计结果，不是权威事实。 */
export function runStats(rows: RunLedgerRow[]): {
  total: number;
  yearCount: number;
  successRate: number;
  avgMinutes: number;
} {
  const total = rows.length;
  const thisYear = new Date().getFullYear();
  const yearCount = rows.filter((r) => new Date(r.startedAt).getFullYear() === thisYear).length;
  const succeeded = rows.filter((r) => r.status === 'success').length;
  const durations = rows.flatMap((r) => (r.durationSeconds === null ? [] : [r.durationSeconds]));
  return {
    total,
    yearCount,
    successRate: total === 0 ? 0 : Math.round((succeeded / total) * 100),
    avgMinutes:
      durations.length === 0
        ? 0
        : Math.round(durations.reduce((a, b) => a + b, 0) / durations.length / 60),
  };
}

/**
 * 卡头的属性徽标行（原型 atcHTML 1530-1536）：类别 ｜ 确定性 ｜ Agent 工作流·N 步 ｜
 * 须人工审批 ｜ 成熟度五格条。
 *
 * **这些属性上了卡头，「资产信息」里就不再重复**——同一个事实在一页上出现两次，
 * 是 SSOT 在呈现层的破口。
 *
 * 类别来自树投影（cls，本体），进来就有；其余四项在版本上（版本档），
 * 读模型没回来之前不渲染——不编占位。
 */
export function CardBadges({
  cls,
  standard,
}: {
  cls: 'operational' | 'construction' | null;
  standard: AtcStandard | null;
}): ReactNode {
  const level = standard === null ? 0 : Number(standard.maturityLevel.replace(/^L/, '')) || 0;
  return (
    <div className="pill-row">
      {cls !== null && (
        <span className={`pill ${cls === 'construction' ? 'pur' : 'blu'}`}>
          {cls === 'construction' ? '建设 ATC' : '运营 ATC'}
        </span>
      )}
      {standard !== null && (
        <>
          <span className={`pill ${standard.determinism === 'deterministic' ? 'ok' : 'red'}`}>
            {DETERMINISM_LABEL[standard.determinism] ?? standard.determinism}
          </span>
          <span className="pill gry">Agent 工作流 · {standard.actionSteps.length} 步</span>
          {standard.hotlGate && <span className="pill amb">须人工审批</span>}
          <span className="mat" aria-label={`成熟度 ${standard.maturityLevel}`}>
            {[0, 1, 2, 3, 4].map((i) => (
              <i key={i} className={i < level ? 'f' : ''} />
            ))}
            <span>{MATURITY_LABEL[standard.maturityLevel] ?? standard.maturityLevel}</span>
          </span>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── 分区 1 · ATC 任务卡（概览） ───────────────────────── */

export function OverviewPanel({
  standard,
  currentVersion,
  runs,
  people,
  infoOverride,
}: {
  standard: AtcStandard | null;
  currentVersion: AtcVersion | null;
  /** null = 还在读。空数组 = 零态：这张卡还没跑过。 */
  runs: RunLedgerRow[] | null;
  people: AtcPersonList | null;
  /** 编辑态：资产信息那一块换成表单（C-190 批二 A6）。 */
  infoOverride?: ReactNode;
}): ReactNode {
  const stats = runs === null ? null : runStats(runs);
  const responsible = people?.rows.filter((r) => r.role === 'responsible' && r.enabled) ?? [];
  return (
    <div className="grid2">
      <div>
        {infoOverride ?? (
        <div className="blk">
          <h3>
            资产信息 <span className="sub">身份 · 归属 · 触发——本体档事实</span>
          </h3>
          {standard === null ? (
            <p className="sub-hint">读取中……</p>
          ) : (
            <dl className="kv">
              <dt>编码</dt>
              <dd className="mono">{standard.code}</dd>
              <dt>名称</dt>
              <dd>{standard.name}</dd>
              {/*
                描述（已裁：留格，标待建）。它是这张卡**长期的业务说明**，随定义演进、
                改了历史执行仍指得对——属本体档，库里还没有这个字段。
                `proposal_summary` 不顶替它：那是**提议时的说明**，入池写一次、改不得，
                属仅追加。生命周期不同的两个东西不能互相顶替。
              */}
              <dt>描述</dt>
              <dd>
                <span className="chip wait">待建</span>{' '}
                <span className="sub-hint">
                  长期的业务说明，随定义演进——字段尚未建；入池时的提议说明是另一条仅追加的事实，不顶替它。
                </span>
              </dd>
              <dt>所属部门</dt>
              <dd>{orFallback(standard.departmentName, '未设定')}</dd>
              <dt>责任岗位</dt>
              <dd>{orFallback(standard.positionName, '未设定')}（一张 ATC 仅一个责任岗位）</dd>
              <dt>数据胶囊域</dt>
              <dd className="mono">{orFallback(standard.dataDomain, '未设定')}</dd>
              <dt>触发</dt>
              <dd>{TRIGGER_LABEL[standard.triggerType] ?? standard.triggerType}</dd>
              {/* 执行许可 / 须审批 / 成熟度在卡头徽标行——同一事实不在这一页出现两次。 */}
              <dt>当前版本</dt>
              <dd>
                {currentVersion === null ? (
                  '—'
                ) : (
                  <>
                    v{currentVersion.versionNo}{' '}
                    <span className="mono sub-hint">{currentVersion.contentHash.slice(0, 12)}</span>
                    {'　'}本版由{authoredByText(currentVersion)}完成
                  </>
                )}
              </dd>
              <dt>责任人</dt>
              <dd>
                {people === null ? (
                  '读取中……'
                ) : responsible.length === 0 ? (
                  <span className="zero">尚未指派责任人</span>
                ) : (
                  responsible.map((r) => r.name).join('、')
                )}
              </dd>
            </dl>
          )}
        </div>
        )}

        <div className="blk">
          <h3>
            计量统计 <span className="sub">统计口径：由「执行台账」逐次记录派生</span>
          </h3>
          {/*
            四个数全是**统计结果**：由执行台账逐次派生，不是填出来的事实。
            没有执行记录时显 0——零态，不是「无源」，更不是编一个数。
          */}
          {stats === null ? (
            <p className="sub-hint">读取中……</p>
          ) : (
            <div className="stat-cells">
              <div className="stat-cell">
                <div className="stat-num">{stats.yearCount}</div>
                <div className="sub-hint">本年执行次数</div>
              </div>
              <div className="stat-cell">
                <div className="stat-num">{stats.total}</div>
                <div className="sub-hint">累计执行次数</div>
              </div>
              <div className="stat-cell">
                <div className="stat-num">{stats.successRate}%</div>
                <div className="sub-hint">成功率</div>
              </div>
              <div className="stat-cell">
                <div className="stat-num">{stats.avgMinutes}</div>
                <div className="sub-hint">平均时长（分钟）</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="blk">
          <h3>
            价值区块 <span className="sub">口径尚未裁定</span>
          </h3>
          {/*
            零态保留，不写编号：价值计量的口径未决。
            裁定落地之前，本区一个数字都不呈现——呈现了就有人拿它当结论。
            虚线块（.val）是「这里有意为空」的形状信号。
          */}
          <div className="val">价值计量的口径尚未裁定。裁定前本区不呈现任何数字。</div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── 分区 2 · Registration · 边界 ───────────────────────── */

export function RegistrationPanel({
  standard,
  currentVersion,
  standards,
}: {
  standard: AtcStandard | null;
  currentVersion: AtcVersion | null;
  standards: AtcStandardList | null;
}): ReactNode {
  return (
    <>
      <div className="blk">
        <h3>
          Registration <span className="sub">这张卡能做什么 · 硬边界</span>
        </h3>
        {standard === null ? (
          <p className="sub-hint">读取中……</p>
        ) : (
          <>
            <dl className="kv">
              <dt>责任岗位</dt>
              <dd>{orFallback(standard.positionName, '未设定')}（唯一）</dd>
              <dt>数据域</dt>
              <dd className="mono">{orFallback(standard.dataDomain, '未设定')}</dd>
              <dt>执行许可</dt>
              <dd>
                {DETERMINISM_LABEL[standard.determinism] ?? standard.determinism}
                {standard.determinism === 'deterministic'
                  ? '——同样的输入必得同样的结论，机器判得出对错'
                  : '——结论要人判断，未开人工审批不得上线'}
              </dd>
              <dt>人工审批点</dt>
              <dd>{standard.hotlGate ? '有——跑到这里必须停下等人批' : '无'}</dd>
              <dt>执行主体</dt>
              <dd>
                {currentVersion?.executorSpec === null ||
                currentVersion?.executorSpec === undefined ? (
                  <span className="zero">
                    未登记——哪一段由人工、哪一段由智能体执行，尚未结构化登记
                  </span>
                ) : (
                  <span className="mono">{JSON.stringify(currentVersion.executorSpec)}</span>
                )}
              </dd>
              <dt>方法论保护</dt>
              <dd>
                {orFallback(
                  currentVersion?.protectionLevel ?? null,
                  '未设定——这一定义的哪一段不给谁看，尚未设定',
                )}
              </dd>
            </dl>

            <div className="seg">
              <h4 className="seg-head">CAN · 允许做什么（能力登记，硬上限）</h4>
              {standard.actionSteps.length === 0 ? (
                <p className="zero">还没填——能力序列为空时，AI 什么都不能做</p>
              ) : (
                <ol className="steps">
                  {standard.actionSteps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              )}
              <p className="sub-hint">仅限以上 ACTION 序列声明的工具、系统与动作；未登记项即越界。</p>
            </div>

            <div className="seg hard">
              <h4 className="seg-head">HARD BOUNDARY · 不得越界</h4>
              <ul className="steps">
                <li>
                  数据访问仅限本卡数据域{' '}
                  <span className="mono">{standard.dataDomain ?? '（未设定）'}</span>
                </li>
                <li>不得调用 ACTION 序列之外的工具与系统</li>
                {standard.hotlGate && <li>人工审批点未批，不得置成功</li>}
                <li>不得反写蓝图；不得修改历史台账</li>
                <li>越界动作即拦下并落台账，不静默重试</li>
              </ul>
            </div>

            <p className="sub-hint">
              <span className="chip plain">留位待裁</span> WA 智能体徽标——工作智能体不在本期范围。
            </p>
          </>
        )}
      </div>

      <div className="blk">
        <h3>
          依据标准 <span className="sub">挂版本不挂本体——历史执行按当时那版的标准受审</span>
        </h3>
        {standards === null ? (
          <p className="sub-hint">读取中……</p>
        ) : standards.rows.length === 0 ? (
          <p className="zero">未登记标准</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>编号</th>
                  <th>版本</th>
                  <th>类型</th>
                  <th>名称</th>
                  <th>发布方</th>
                </tr>
              </thead>
              <tbody>
                {standards.rows.map((s) => (
                  <tr key={s.standardRefId}>
                    <td className="mono">{s.standardNo}</td>
                    <td className="mono">{s.version ?? '—'}</td>
                    <td>{s.kind ?? '—'}</td>
                    <td>{s.title}</td>
                    <td>{s.publisher ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ───────────────────────── 分区 3 · 定义 · 四段（工作流） ───────────────────────── */

export function DefinitionPanel({
  standard,
  currentVersion,
}: {
  standard: AtcStandard | null;
  currentVersion: AtcVersion | null;
}): ReactNode {
  if (standard === null) return <p className="sub-hint">读取中……</p>;
  const testEmpty =
    standard.testSpec === null ||
    standard.testSpec === undefined ||
    JSON.stringify(standard.testSpec) === '{}';
  return (
    <div className="blk">
      <h3>
        任务定义{' '}
        {currentVersion !== null && (
          <span className="sub">
            当前版本 v{currentVersion.versionNo} · 本版由{authoredByText(currentVersion)}完成 · 内容指纹{' '}
            <span className="mono">{currentVersion.contentHash.slice(0, 12)}</span>
          </span>
        )}
      </h3>
      <div className="seg">
        <h4 className="seg-head">WHAT · 目标与边界</h4>
        <p>{orFallback(standard.what, '还没填')}</p>
      </div>
      <div className="seg">
        <h4 className="seg-head">HOW · 方法与判据</h4>
        <p>{orFallback(standard.how, '还没填')}</p>
      </div>
      <div className="seg">
        <h4 className="seg-head">ACTION · 可调用工具、系统与动作序列</h4>
        {standard.actionSteps.length === 0 ? (
          <p className="zero">还没填</p>
        ) : (
          <ol className="steps">
            {standard.actionSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        )}
      </div>
      <div className="seg test">
        <h4 className="seg-head">TEST · 客观验证判据（永不折叠）</h4>
        {testEmpty ? (
          <p className="zero">还没填——TEST 没有可判定断言，这张卡进不了「定义认证」</p>
        ) : (
          <p className="mono">{JSON.stringify(standard.testSpec)}</p>
        )}
      </div>
      <p className="sub-hint">
        定义仅追加：改四段是提一版新的，不是原地改。历史版本见「蓝图与版本」。
      </p>
    </div>
  );
}

/* ───────────────────────── 分区 4 · SOP 全流程 ───────────────────────── */

export function SopPanel({ standard }: { standard: AtcStandard | null }): ReactNode {
  if (standard === null) return <p className="sub-hint">读取中……</p>;
  return (
    <div className="blk">
      <h3>
        SOP 全流程 <span className="sub">HOW 与 ACTION 的环节级展开；判据以 TEST 段为准</span>
      </h3>
      {standard.actionSteps.length === 0 ? (
        <p className="zero">还没填——ACTION 序列为空，环节无从展开</p>
      ) : (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>环节</th>
                  <th>做什么</th>
                  <th>关键要点 / 异常处置</th>
                </tr>
              </thead>
              <tbody>
                {standard.actionSteps.map((step, i) => (
                  <tr key={i}>
                    <td className="mono">{i + 1}</td>
                    <td>环节 {i + 1}</td>
                    <td>{step}</td>
                    <td>
                      <span className="chip wait">待建</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ToBeBuilt>
            「关键要点 / 异常处置」一列：模型已确认它该落成 ACTION
            段的结构化子属性（超出 HOW 文本容量），尚未建。
          </ToBeBuilt>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── 分区 5 · ACTION 包与调用登记 ───────────────────────── */

export function ActionPackagePanel({
  currentVersion,
}: {
  currentVersion: AtcVersion | null;
}): ReactNode {
  return (
    <>
      <div className="blk">
        <h3>
          ACTION 包 <span className="sub">包引用挂版本；包本体的登记结构尚未建立</span>
        </h3>
        <dl className="kv">
          <dt>包引用</dt>
          <dd>
            {currentVersion === null
              ? '读取中……'
              : orFallback(currentVersion.actionPackageRef, '未登记')}
          </dd>
        </dl>
        <p className="sub-hint">
          引用列先在表上，包本身（技能清单、去向、授权）要等包本体的登记结构建立。
        </p>
      </div>

      <div className="blk">
        <h3>
          调用登记 <span className="sub">每一次执行调用了哪些外部能力</span>
        </h3>
        <ToBeBuilt>
          过程消息流与调用链：过程层已确认需要，承载它的「一次执行」实体尚未建——
          建好前这里不编内容。
        </ToBeBuilt>
      </div>
    </>
  );
}

/* ───────────────────────── 分区 6 · 人员与责任 ───────────────────────── */

export function PeoplePanel({ people }: { people: AtcPersonList | null }): ReactNode {
  if (people === null) return <p className="sub-hint">读取中……</p>;
  const c = people.count;
  return (
    <div className="blk">
      <h3>
        人员与责任 <span className="sub">每次执行落一名责任人——谁调用谁负责</span>
      </h3>
      {c.responsibleCount === 0 && <p className="zero">尚未指派责任人</p>}
      {people.rows.length === 0 ? (
        <p className="sub-hint">
          这张卡还没有登记人员。岗位成员默认即使用者；例外与叠加记在这张表上。
        </p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>姓名</th>
                <th>角色</th>
                <th>生效起</th>
                <th>状态</th>
              </tr>
            </thead>
            <tbody>
              {people.rows.map((r) => (
                <tr key={r.atcPersonId}>
                  <td>{r.name}</td>
                  <td>{ROLE_LABEL[r.role] ?? r.role}</td>
                  <td className="mono">{r.effectiveFrom.slice(0, 10)}</td>
                  <td>
                    {r.enabled ? (
                      <span className="chip ok">启用</span>
                    ) : (
                      <span className="chip wait">已停用</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="sub-hint">
        在册：责任人 {c.responsibleCount} · 使用者 {c.userCount} · 评价者 {c.evaluatorCount}
        （视图统计）。角色语义：责任人——每次执行落一名；使用者——可发起本卡执行的人；
        评价者——对结果有评价权。停用 = 暂不参与触达与考评路由，不影响历史台账中该人的既有责任记录。
      </p>
    </div>
  );
}

/* ───────────────────────── 分区 7 · 数据链接 ───────────────────────── */

export function DataLinksPanel({
  standard,
  inputs,
}: {
  standard: AtcStandard | null;
  inputs: AtcInput[] | null;
}): ReactNode {
  return (
    <>
      <div className="blk">
        <h3>
          输入登记 <span className="sub">材料按 ID 可寻址</span>
        </h3>
        {inputs === null ? (
          <p className="sub-hint">读取中……</p>
        ) : inputs.length === 0 ? (
          <p className="zero">还没有登记输入材料</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>材料</th>
                  <th>载体</th>
                  <th>来源</th>
                  <th>登记时间</th>
                  <th>材料 ID</th>
                </tr>
              </thead>
              <tbody>
                {inputs.map((m) => (
                  <tr key={m.materialId}>
                    <td>{m.originalName ?? m.sourcePath ?? '—'}</td>
                    <td>{m.carrierKind}</td>
                    <td>{m.provenance}</td>
                    <td className="mono">{m.registeredAt.slice(0, 10)}</td>
                    <td className="mono">{m.materialId.slice(0, 12)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="sub-hint">
          数据胶囊域：<span className="mono">{standard?.dataDomain ?? '未设定'}</span>
          。材料按 ID 可寻址；「这一次执行用了哪些输入」属执行级，见「执行台账」的待建标记。
        </p>
      </div>

      <div className="blk">
        <h3>
          归属记录 <span className="sub">按载体分布的记录计数</span>
        </h3>
        <ToBeBuilt>
          归属表已在，但「哪个动作产生归属」的写入方尚未建——建好后这里显示按载体的记录分布。
        </ToBeBuilt>
      </div>

      <div className="blk">
        <h3>
          数据去处 <span className="sub">结果回写到哪个外部系统、是否成功</span>
        </h3>
        <ToBeBuilt>
          「结果回写到了哪个外部系统、是否成功」在模型里还没有列——已确认需要，尚未建。
        </ToBeBuilt>
        <ToBeBuilt>
          外部载荷到卡字段的配置映射规则：两个已观测实例里都没有，本次未观测到——
          按「已确认需要」登记为待建。载荷自带的显示名表若有材料携带，会在输入登记中显示。
        </ToBeBuilt>
      </div>
    </>
  );
}

/* ───────────────────────── 分区 8 · 执行台账 ───────────────────────── */

export function RunsPanel({ runs }: { runs: RunLedgerRow[] | null }): ReactNode {
  if (runs === null) return <p className="sub-hint">读取中……</p>;
  const stats = runStats(runs);
  return (
    <div className="blk">
      <h3>
        执行台账 <span className="sub">仅追加：每一次执行有账可查</span>
      </h3>
      <p className="sub-hint">
        共 {stats.total} 次 · 本年 {stats.yearCount} 次 · 成功率 {stats.successRate}
        %（统计，由下列台账逐次派生）。
      </p>
      {runs.length === 0 ? (
        <p className="zero">这张卡还没有执行记录。第一次执行发生后，这里会自己出现。</p>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>开始</th>
                <th>耗时</th>
                <th>执行方</th>
                <th>责任人</th>
                <th>触发情境</th>
                <th>结果</th>
                <th>TEST</th>
                <th>关键输出</th>
                <th>产出</th>
                <th>run_ref</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.runRef}>
                  <td className="mono">{r.startedAt.slice(0, 16).replace('T', ' ')}</td>
                  <td className="mono">
                    {r.durationSeconds === null ? '—' : `${Math.round(r.durationSeconds / 60)} min`}
                  </td>
                  <td>{ACTOR_LABEL[r.actorType] ?? r.actorType}</td>
                  <td>{r.responsible ?? '—'}</td>
                  <td>{r.triggerRef ?? '—'}</td>
                  <td>{r.status}</td>
                  <td>{r.testResult}</td>
                  <td>{r.keyOutput ?? '—'}</td>
                  <td className="mono">{r.artifactCount}</td>
                  <td className="mono">{r.runRef}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ToBeBuilt>
        一次执行的闭包：这一次执行的输入集、产出集、审批与责任人落定，将挂在「一次执行」
        实体上——四类读者都需要它，表尚未建。
      </ToBeBuilt>
      <ToBeBuilt>
        执行级状态历史：卡级状态历史已有，执行级（这一次执行走到哪一格）尚未建。
      </ToBeBuilt>
    </div>
  );
}

/* ───────────────────────── 分区 9 · 蓝图与版本 ───────────────────────── */

export function BlueprintPanel({ standard }: { standard: AtcStandard | null }): ReactNode {
  if (standard === null) return <p className="sub-hint">读取中……</p>;
  return (
    <div className="blk">
      <h3>
        蓝图 <span className="sub">实例只读继承，永不反写</span>
      </h3>
      {standard.blueprintRef === null ? (
        <p className="zero">本卡未继承蓝图（本地新建）。</p>
      ) : (
        <dl className="kv">
          <dt>来源蓝图</dt>
          <dd className="mono">{standard.blueprintRef}</dd>
          <dt>继承版本</dt>
          <dd className="mono">{standard.blueprintVersion ?? '—'}</dd>
          <dt>回写入口</dt>
          <dd>无——本页不提供任何向蓝图回写的操作</dd>
        </dl>
      )}
      <p className="sub-hint">
        蓝图库本体与三级继承（企业 → 部门 → 岗位）尚未建，属后续范围；
        这张卡上的引用字段已在，蓝图建好之后这里显示的就是它继承的那一份。只读不回写。
      </p>
    </div>
  );
}
